import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { Badge } from '../../components/ui/badge';
import { 
  Users, 
  Calendar, 
  FileText, 
  TrendingUp, 
  Megaphone, 
  Mail, 
  Archive, 
  Briefcase,
  Building2,
  UserPlus,
  ScrollText,
  ClipboardList,
  AlertTriangle
} from 'lucide-react';

// İK Paneli bileşenlerini import et - aynı görünüm için
import EmployeeList from '../hr/EmployeeList';
import Communications from '../hr/Communications';
import Documents from '../hr/Documents';
import Announcements from '../hr/Announcements';
import HRCommunication from '../hr/HRCommunication';
import WorkCalendar from '../work/WorkCalendar';
import HRArchive from '../hr/HRArchive';
import HRJobs from '../hr/HRJobs';
import TalentPool from '../hr/TalentPool';
import HRRules from '../hr/HRRules';
import HRPersonnelRequests from '../hr/HRPersonnelRequests';
import HRWarnings from '../hr/HRWarnings';

const API = process.env.REACT_APP_BACKEND_URL;

const BoardBirimIKContent = () => {
  const { user } = useAuth();
  const { getBadgeCount, markSeen } = useBadges();
  const [activeMenu, setActiveMenu] = useState('employees');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [communicationsTotalUnread, setCommunicationsTotalUnread] = useState(0);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Genel bildirim sayılarını al
      const response = await fetch(`${API}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
      
      // İK İletişim toplam okunmamış sayısını al
      const commResponse = await fetch(`${API}/api/hr/communications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (commResponse.ok) {
        const commData = await commResponse.json();
        setCommunicationsTotalUnread(commData.total || 0);
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Menu Items - HRPanel ile AYNI
  const menuItems = [
    { key: 'employees', icon: Users, label: 'Çalışanlar', menuKey: 'employees', badgeKey: null },
    { key: 'calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
    { key: 'jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: null },
    { key: 'communication', icon: Mail, label: 'İletişim', menuKey: 'communication', badgeKey: 'messages' },
    { key: 'announcements', icon: Megaphone, label: 'Duyurular', menuKey: 'announcements', badgeKey: null },
    { key: 'documents', icon: FileText, label: 'Belgeler', menuKey: 'documents', badgeKey: null },
    { key: 'performance', icon: TrendingUp, label: 'Performans', menuKey: 'performance', disabled: true, badgeKey: null },
    { key: 'archive', icon: Archive, label: 'Dosyalar', menuKey: 'archive', badgeKey: null },
    { key: 'talentpool', icon: UserPlus, label: 'Talent Pool', menuKey: 'talentpool', badgeKey: null },
    { key: 'rules', icon: ScrollText, label: 'Kurallar', menuKey: 'rules', badgeKey: null },
    { key: 'personnel_requests', icon: ClipboardList, label: 'Personel Talepleri', menuKey: 'personnel_requests', badgeKey: null },
    { key: 'warnings', icon: AlertTriangle, label: 'Uyarılar', menuKey: 'warnings', badgeKey: null },
  ];

  // İçerik render - İK Paneli bileşenlerini doğrudan kullan
  const renderContent = () => {
    switch (activeMenu) {
      case 'employees':
        return <EmployeeList />;
      case 'calendar':
        return <WorkCalendar enableHolidays={true} />;
      case 'jobs':
        return <HRJobs />;
      case 'personnel_requests':
        return <HRPersonnelRequests />;
      case 'warnings':
        return <HRWarnings />;
      case 'communication':
        return <HRCommunication />;
      case 'announcements':
        return <Announcements />;
      case 'documents':
        return <Documents />;
      case 'archive':
        return <HRArchive />;
      case 'talentpool':
        return <TalentPool />;
      case 'rules':
        return <HRRules />;
      case 'performance':
        return (
          <div className="text-center py-12 text-gray-500">
            <TrendingUp className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">Performans modülü yakında aktif olacak</p>
          </div>
        );
      default:
        return <EmployeeList />;
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="board-birim-ik">
      {/* Header - HRPanel ile aynı stil */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/70 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-blue-600">İnsan Kaynakları</span>
            </div>
            <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-700 border-blue-300">Salt Okunur</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - HRPanel ile AYNI */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              // Badge sayısını belirle
              let badgeCount = getBadgeCount(item.menuKey);
              if (badgeCount === 0) {
                if (item.badgeKey === 'communications_total' && communicationsTotalUnread > 0) {
                  badgeCount = communicationsTotalUnread;
                } else if (item.badgeKey && unreadCounts[item.badgeKey] > 0) {
                  badgeCount = unreadCounts[item.badgeKey];
                }
              }
              
              return (
                <button
                  key={item.key}
                  onClick={() => !item.disabled && setActiveMenu(item.key)}
                  disabled={item.disabled}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                      : activeMenu === item.key
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.disabled && <span className="ml-auto text-xs">(Yakında)</span>}
                  {!item.disabled && badgeCount > 0 && (
                    <Badge className={`ml-auto ${
                      activeMenu === item.key ? 'bg-white text-blue-600' : 'bg-red-500 text-white'
                    }`}>
                      {badgeCount}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content - İK Paneli bileşenlerini göster */}
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

const BoardBirimIK = () => {
  return (
    <BadgeProvider panel="hr">
      <BoardBirimIKContent />
    </BadgeProvider>
  );
};

export default BoardBirimIK;
